/* Testimonials slider — scroll-snap driven, no dependencies. */
if (!customElements.get('custom-testimonials-slider')) {
  customElements.define(
    'custom-testimonials-slider',
    class CustomTestimonialsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-ct-track]');
        if (!this.track) return;

        this.items = Array.from(this.track.children);
        this.prevButton = this.querySelector('[data-ct-prev]');
        this.nextButton = this.querySelector('[data-ct-next]');
        this.dots = Array.from(this.querySelectorAll('[data-ct-dot]'));

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.current = 0;

        this.onScroll = this.throttle(this.syncToScroll.bind(this), 100);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
        this.dots.forEach((dot) =>
          dot.addEventListener('click', () => this.goTo(Number(dot.dataset.ctDot)))
        );

        this.syncToScroll();
        this.maybeStartAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      }

      /* How many items fit in the viewport at once — a 3-up slider should not
         allow scrolling past the last full page of cards. */
      get perView() {
        if (!this.items.length) return 1;
        const itemWidth = this.items[0].getBoundingClientRect().width;
        if (!itemWidth) return 1;
        return Math.max(1, Math.round(this.track.clientWidth / itemWidth));
      }

      get lastIndex() {
        return Math.max(0, this.items.length - this.perView);
      }

      step(direction) {
        let next = this.current + direction;
        if (next < 0) next = this.lastIndex;
        if (next > this.lastIndex) next = 0;
        this.goTo(next);
      }

      /* Measure against the track's own box rather than offsetLeft: the items'
         offsetParent is the wrapper, not the track, and the track carries padding
         for focus rings — both of which skew offsetLeft arithmetic. */
      offsetOf(item) {
        const delta = item.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        const padding = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        return this.track.scrollLeft + delta - padding;
      }

      goTo(index) {
        const item = this.items[index];
        if (!item) return;
        this.track.scrollTo({
          left: this.offsetOf(item),
          behavior: this.reducedMotion ? 'auto' : 'smooth',
        });
      }

      /* Scroll position is the source of truth — this keeps dots and arrows
         correct after a swipe, a keyboard scroll, or a resize. */
      syncToScroll() {
        const trackLeft = this.track.getBoundingClientRect().left;
        const padding = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        let closest = 0;
        let closestDistance = Infinity;

        this.items.forEach((item, index) => {
          // Distance from the track's content edge, in viewport space.
          const distance = Math.abs(item.getBoundingClientRect().left - trackLeft - padding);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = index;
          }
        });

        this.current = Math.min(closest, this.lastIndex);
        this.updateControls();
      }

      updateControls() {
        this.dots.forEach((dot, index) => {
          dot.setAttribute('aria-selected', index === this.current ? 'true' : 'false');
          dot.hidden = index > this.lastIndex;
        });
      }

      maybeStartAutoplay() {
        if (this.dataset.autoplay !== 'true' || this.reducedMotion) return;
        if (this.items.length <= this.perView) return;

        const delay = (Number(this.dataset.autoplayDelay) || 6) * 1000;
        this.timer = setInterval(() => this.step(1), delay);

        // Pause while the visitor is interacting, resume when they leave.
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      }

      stopAutoplay() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      throttle(fn, wait) {
        let queued = false;
        return (...args) => {
          if (queued) return;
          queued = true;
          setTimeout(() => {
            queued = false;
            fn(...args);
          }, wait);
        };
      }
    }
  );
}
